// Assignment Completion Exclusions (ADR-108)
//
// Which engagement models are NOT measured by the Assignment Completion
// report, card and service-unit detail page, for a given year. A scope rule —
// "is this person measured at all?" — as opposed to the Assignment Completion
// Policies, which say what is expected of a person who IS measured.
//
// Organization-wide and per year: the list applies to every Cluster,
// policy or no policy, and a past year keeps the list it was measured with.
// People on an excluded model still appear on the report — in the Skipped
// column, tagged with the model — so a headcount always explains itself.
//
// The page lists EVERY engagement model from the Organization taxonomy with a
// Measured / Not measured switch and how many people the switch touches, and
// saves the whole set in one request: the set is the configuration, not the
// individual rows, so it cannot be left half-applied.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Switch,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, MainSection, BackButton } from '../../common/styles';
import {
  FiltersBar,
  GridHeader,
  ModelRow,
  SwitchCell,
  Actions,
  Hint,
} from './styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ConfirmationModal from '../../common/ConfirmationModal';
import { useAlert } from '../../../contexts/AlertContext';
import { assignmentCompletionApi } from '../../../services/api/assignmentCompletionApi';

const AssignmentCompletionExclusions = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const [year, setYear] = useState(currentYear);
  const [models, setModels] = useState([]);
  // The saved set and the draft set, as ids. Dirty = they differ.
  const [savedIds, setSavedIds] = useState(new Set());
  const [draftIds, setDraftIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [copyConfirmOpen, setCopyConfirmOpen] = useState(false);

  // Same window as the policies page: next year (to prepare) back to three
  // years of history.
  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear + 1; y >= currentYear - 3; y--) {
      years.push(y);
    }
    return years;
  }, [currentYear]);

  const applyResponse = useCallback((data) => {
    const list = data?.engagementModels || [];
    setModels(list);
    const excluded = new Set(list.filter((model) => model.excluded).map((model) => model.id));
    setSavedIds(excluded);
    setDraftIds(new Set(excluded));
  }, []);

  const load = useCallback(async (targetYear) => {
    try {
      setIsLoading(true);
      const data = await assignmentCompletionApi.getExclusions(targetYear);
      applyResponse(data);
    } catch (error) {
      showAlert('Failed to load assignment completion exclusions', 'error', error);
    } finally {
      setIsLoading(false);
    }
  }, [applyResponse, showAlert]);

  useEffect(() => {
    load(year);
  }, [load, year]);

  const toggle = (modelId) => {
    setDraftIds((prev) => {
      const next = new Set(prev);
      if (next.has(modelId)) {
        next.delete(modelId);
      } else {
        next.add(modelId);
      }
      return next;
    });
  };

  const isDirty = useMemo(() => {
    if (savedIds.size !== draftIds.size) {
      return true;
    }
    for (const id of draftIds) {
      if (!savedIds.has(id)) {
        return true;
      }
    }
    return false;
  }, [savedIds, draftIds]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const data = await assignmentCompletionApi.saveExclusions({
        year,
        engagementModelIds: [...draftIds],
      });
      applyResponse(data);
      showAlert(`Exclusions saved for ${year}`, 'success');
    } catch (error) {
      showAlert('Failed to save exclusions', 'error', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setDraftIds(new Set(savedIds));
  };

  const handleCopy = async () => {
    setCopyConfirmOpen(false);
    try {
      setIsSaving(true);
      const data = await assignmentCompletionApi.copyExclusions({ fromYear: year - 1, toYear: year });
      applyResponse(data);
      showAlert(`${year - 1}'s exclusions added to ${year}`, 'success');
    } catch (error) {
      showAlert('Failed to copy exclusions', 'error', error);
    } finally {
      setIsSaving(false);
    }
  };

  // What the draft would do, so the hint states the consequence rather than
  // the count of switches: "3 models · 41 people not measured".
  const draftExcludedCount = draftIds.size;
  const draftPeopleCount = models
    .filter((model) => draftIds.has(model.id))
    .reduce((sum, model) => sum + (model.allocatedCount || 0), 0);

  return (
    <Container>
      <Header>
        <HeaderLeft>
          {/* Home, as the policies page does: a config page reached from the
              menu, not from Feedback Sessions. */}
          <BackButton onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>Assignment Completion Exclusions</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <MainSection>
          <FiltersBar>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
                {yearOptions.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Hint>
              {draftExcludedCount === 0
                ? `Every engagement model is measured in ${year}`
                : `${draftExcludedCount} ${draftExcludedCount === 1 ? 'model' : 'models'} not measured in ${year} · ${draftPeopleCount} ${draftPeopleCount === 1 ? 'person' : 'people'} affected`}
              {isDirty && ' · unsaved changes'}
            </Hint>

            <Actions>
              <Tooltip title={`Add ${year - 1}'s excluded models to ${year}. Nothing already excluded in ${year} is removed.`}>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setCopyConfirmOpen(true)}
                    disabled={isSaving || isLoading || isDirty}
                  >
                    Copy from {year - 1}
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="text"
                size="small"
                onClick={handleDiscard}
                disabled={!isDirty || isSaving}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={!isDirty || isSaving}
                startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : null}
              >
                Save
              </Button>
            </Actions>
          </FiltersBar>

          {isLoading ? (
            <LoadingSpinner />
          ) : models.length === 0 ? (
            <EmptyState
              title="No engagement models"
              description="Configure Individual Engagement Models under Organization first — there is nothing to exclude yet."
            />
          ) : (
            <>
              <GridHeader>
                <span>Engagement model</span>
                <Tooltip title={`Consultants on this model who hold a full-time allocation to a business unit's project at any point in ${year}. This is who the switch affects; new joiners and leavers among them are included, because the exclusion applies before those rules.`}>
                  <span className="num help">People affected</span>
                </Tooltip>
                <Tooltip title="Off means consultants on this model are not measured: they are left out of Expected, Completed and Missing everywhere, and appear under Skipped with the model named.">
                  <span className="num help">Measured</span>
                </Tooltip>
              </GridHeader>

              {models.map((model) => {
                const excluded = draftIds.has(model.id);
                const dirty = excluded !== savedIds.has(model.id);
                const className = [excluded ? 'excluded' : '', dirty ? 'dirty' : ''].join(' ').trim();
                return (
                  <ModelRow key={model.id} className={className}>
                    <div className="model">
                      <span>{model.name}</span>
                      {model.code && <span className="code">{model.code}</span>}
                    </div>
                    <div className={`num${model.allocatedCount ? '' : ' muted'}`}>
                      {model.allocatedCount || 0}
                    </div>
                    <SwitchCell>
                      <span>{excluded ? 'Not measured' : 'Measured'}</span>
                      <Switch
                        size="small"
                        checked={!excluded}
                        onChange={() => toggle(model.id)}
                        disabled={isSaving}
                        inputProps={{ 'aria-label': `${model.name} measured in ${year}` }}
                      />
                    </SwitchCell>
                  </ModelRow>
                );
              })}

              <Hint className="footer">
                Exclusions apply organization-wide, to every cluster whether or not it has an
                Assignment Completion Policy, and only to the selected year. A consultant&apos;s engagement
                model is read as it is today — there is no history — so reclassifying someone changes past
                years too. Excluded consultants are still shown on the report, under Skipped, tagged with
                the model that excluded them.
              </Hint>
            </>
          )}
        </MainSection>
      </Content>

      <ConfirmationModal
        isOpen={copyConfirmOpen}
        onClose={() => setCopyConfirmOpen(false)}
        onConfirm={handleCopy}
        title={`Copy exclusions from ${year - 1}`}
        message={`Every engagement model excluded in ${year - 1} will also be excluded in ${year}. Models already excluded in ${year} stay excluded — nothing is removed.`}
        actionButtonText="Copy"
      />
    </Container>
  );
};

export default AssignmentCompletionExclusions;
